import type {
  CreateStagingRequest,
  CreateStagingResponse,
  UpdateStagingRequest,
  UpdateStagingResponse,
  CompleteDatasetRequest,
  CompleteDatasetResponse,
  GetStagingListResponse,
  GetStagingResponse,
  ErrorResponse,
} from '@/shared/types/staging';

import { logger } from '@/shared/lib/logger';
import { supabase } from '@/shared/lib/supabase';
import type { ApiResponse } from '@/shared/types/api';

import { apiClient } from './client';

// Types for staging media upload
export type UploadStagingMediaRequest = {
  stagingId: number;
  name: string;
  originalName: string;
  extension: 'jpg' | 'png' | 'webp';
};

export type MediaStagingData = {
  id: number;
  name: string;
  url: string;
  urlThumbnail: string;
  extension: string;
  stagingId: number;
};

export type UploadStagingMediaResponse = ApiResponse & {
  data: {
    mediaStaging: MediaStagingData;
  };
};

export class StagingService {
  // Storage bucket name - matches S3_BUCKET_NAME=famecreator
  private static readonly STORAGE_BUCKET = 'famecreator';

  /**
   * Upload file to Supabase Storage
   */
  private static async uploadFileToStorage(file: File, filePath: string): Promise<string> {
    try {
      const { error } = await supabase.storage
        .from(StagingService.STORAGE_BUCKET)
        .upload(filePath, file, {
          upsert: false, // Don't overwrite existing files
        });

      if (error) {
        throw new Error(`Storage upload failed: ${error.message}`);
      }

      logger.api.debug('File uploaded successfully', { filePath });
      return filePath;
    } catch (error: unknown) {
      logger.api.error('Error uploading file to storage', { error, filePath });
      throw error;
    }
  }

  /**
   * Generate file path for staging media
   * Format: user/{userId}/staging/{stagingId}/source/{fileName}
   */
  static generateFilePath(fileName: string, stagingId: number, userId: string): string {
    return `user/${userId}/staging/${stagingId}/source/${fileName}`;
  }

  /**
   * Generate unique file name to prevent conflicts
   * Format: {uuid}.{extension}
   */
  private generateUniqueFileName(originalName: string): string {
    const extension = this.getFileExtension(originalName);
    const uuid = crypto.randomUUID();
    return `${uuid}.${extension}`;
  }

  /**
   * Get file extension from filename
   */
  private getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || 'jpg';
  }

  /**
   * Validate file before upload
   */
  private validateFileForUpload(file: File): string | null {
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return 'File size exceeds 10MB';
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Only JPG, PNG, and WebP are allowed';
    }

    return null;
  }

  /**
   * Get list of all staging
   * GET /staging
   */
  async getStagingList(): Promise<GetStagingListResponse> {
    try {
      const response = await apiClient.get<GetStagingListResponse>('/staging');
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get staging by ID
   * GET /staging/:id
   */
  async getStaging(stagingId: number): Promise<GetStagingResponse> {
    try {
      const response = await apiClient.get<GetStagingResponse>(`/staging/${stagingId}`);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Create a new staging
   * POST /staging
   */
  async createStaging(request: CreateStagingRequest): Promise<CreateStagingResponse> {
    try {
      const response = await apiClient.post<CreateStagingResponse>('/staging', request);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Update a staging
   * PATCH /staging/{id}
   */
  async updateStaging(stagingId: number, request: UpdateStagingRequest): Promise<UpdateStagingResponse> {
    try {
      const response = await apiClient.patch<UpdateStagingResponse>(`/staging/${stagingId}`, request);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Complete dataset generation for a staging
   * POST /staging/{id}/complete-dataset
   */
  async completeDataset(
    stagingId: number,
    request: CompleteDatasetRequest,
  ): Promise<CompleteDatasetResponse> {
    try {
      const response = await apiClient.post<CompleteDatasetResponse>(
        `/staging/${stagingId}/complete-dataset`,
        request,
      );
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Upload media for staging
   * POST /staging/media
   */
  async uploadStagingMedia(data: UploadStagingMediaRequest): Promise<UploadStagingMediaResponse> {
    try {
      const response = await apiClient.post<UploadStagingMediaResponse>('/staging/media', data);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Upload multiple staging media files (max 10)
   */
  async uploadMultipleStagingMedia(
    files: File[],
    stagingId: number,
    userId: string,
    onProgress?: (current: number, total: number, fileName: string) => void,
  ): Promise<{
    successful: MediaStagingData[];
    failed: { file: File; error: string }[];
  }> {
    const successful: MediaStagingData[] = [];
    const failed: { file: File; error: string }[] = [];

    const totalFiles = files.length;

    // Process files sequentially
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        // Report progress for current file
        onProgress?.(i + 1, totalFiles, file.name);

        // Validate file before upload
        const validationError = this.validateFileForUpload(file);
        if (validationError) {
          failed.push({ file, error: validationError });
          continue;
        }

        // Generate unique file name and path
        const uniqueFileName = this.generateUniqueFileName(file.name);
        const filePath = StagingService.generateFilePath(uniqueFileName, stagingId, userId);

        // 1. Upload actual file to Supabase Storage first
        await StagingService.uploadFileToStorage(file, filePath);

        // 2. Then create media record via API
        const response = await this.uploadStagingMedia({
          stagingId,
          name: uniqueFileName,
          originalName: file.name,
          extension: this.getFileExtension(file.name) as 'jpg' | 'png' | 'webp',
        });

        if (response.data?.mediaStaging) {
          successful.push(response.data.mediaStaging);
        } else {
          failed.push({ file, error: 'No media data returned from API' });
        }
      } catch (error: unknown) {
        failed.push({
          file,
          error: error instanceof Error ? error.message : 'Upload failed',
        });
      }
    }

    return { successful, failed };
  }
}

export const stagingService = new StagingService();
